import type { GuideBand } from "./bands.js";
import { GUIDE_CARD_LIBRARY, type GuideCard } from "./cards.js";
import type { JourneyItem, JourneyResult } from "./journey.js";
import { sortGuideItems } from "./sort.js";
import type { GuideTrigger } from "./triggers.js";

/**
 * 🔴🔴 US-AIV2-tourguide-0006 (@UC0370 · TG-INV-9) — LỜI CHÀO = thẻ TRẠNG THÁI chọn theo bậc, rồi thẻ SỰ KIỆN NỐI SAU.
 *
 * Trước task này lượt mở hỏi lần lượt `MY_ROLE_CHANGED` → `CONFIG_CHANGED` → `STALE` → `OPEN` và lấy cái ĐẦU TIÊN có thẻ
 * (`firstLoud`). Hai lỗi đo được (máy đo `0001`): thành viên còn nợ chỉ nghe "vai vừa đổi" (K07 — thẻ HYGIENE đè thẻ DUTY);
 * ngược lại, chỉ chọn bậc cao nhất thì thẻ "tài khoản nhận tiền vừa đổi" (HYGIENE) bị lọc mất trong khi mốc "đã xem" vẫn đẩy
 * ⇒ cảnh báo chống lừa mất vĩnh viễn, đúng lúc người ta sắp chuyển tiền (review epic CRIT-1).
 *
 * Luật: MỘT lần chọn theo bậc cho thẻ TRẠNG THÁI (`OPEN`/`RETURN_AFTER_ABSENCE` — `@UC0182` giữ nguyên); phiếu bỏ dở (`STALE`) gộp vào khối trạng
 * thái (không bị bậc lọc, sắp cùng luật bậc → `priority` — GUIDE-FIX-20261020 S1), rồi thẻ SỰ KIỆN đứng SAU, không bị bậc lọc. Hàm thuần — tầng
 * api gọi `journey()` trên CÙNG một ngữ cảnh.
 */

/** Trigger SỰ KIỆN của lời chào — mốc "đã xem" của chúng theo (người, nhóm, thẻ) (`AiNudgeStateService.eventWatermarks`). */
export const GUIDE_EVENT_TRIGGERS = ["MY_ROLE_CHANGED", "CONFIG_CHANGED"] as const satisfies readonly GuideTrigger[];
export type GuideEventTrigger = (typeof GUIDE_EVENT_TRIGGERS)[number];

/**
 * Thẻ duy nhất trả lời một trigger sự kiện — TỰ KHÁM PHÁ từ thư viện (không chép id tay). ≠ 1 thẻ ⇒ ném: mốc theo thẻ chỉ có nghĩa khi
 * mỗi sự kiện có đúng một thẻ nói nó (hai thẻ cùng sự kiện thì "đã hiện" của thẻ này không nói gì về thẻ kia).
 */
export function guideEventCardId(trigger: GuideEventTrigger, library: readonly GuideCard[] = GUIDE_CARD_LIBRARY): string {
  const cards = library.filter((c) => c.triggers.includes(trigger));
  if (cards.length !== 1) throw new Error(`GUIDE_EVENT_CARD_NOT_UNIQUE: ${trigger} có ${String(cards.length)} thẻ`);
  return cards[0]!.id;
}

/** Id thẻ của MỌI trigger sự kiện (thứ tự = `GUIDE_EVENT_TRIGGERS`). */
export const GUIDE_EVENT_CARD_IDS: readonly string[] = Object.freeze(GUIDE_EVENT_TRIGGERS.map((t) => guideEventCardId(t)));

/**
 * Ghép lời chào. 🔴 GUIDE-FIX-20261020 S1 (@UC0365 TG-INV-6 · @UC0371 TG-INV-10): kết quả nối thêm chia hai loại theo TRIGGER của nó —
 *  - **SỰ KIỆN** (`GUIDE_EVENT_TRIGGERS`) ⇒ đứng SAU, theo thứ tự đưa vào, không bị bậc lọc (TG-INV-9);
 *  - **còn lại** (phiếu bỏ dở `STALE`) là một phần TRẠNG THÁI của người dùng ⇒ gộp vào thẻ `state` rồi sắp CÙNG luật bậc → `priority` của `journey()`
 *    (`sortGuideItems`) — TRƯỚC trần nút thẻ của `settleGuideText`. Bản cũ nối nó cuối ⇒ lời chào có ≥3 thẻ có nút làm mất cả câu lẫn nút «Làm tiếp»,
 *    và lời chào xếp khác lượt "làm gì tiếp" (thẻ `RESUME-UNFINISHED` có cả trigger `ASKED_NEXT`, sắp đầu). Không bị bậc của `state` lọc (luật cũ giữ).
 * Thẻ trùng id chỉ một lần. Bậc của lượt = bậc của thẻ trạng thái đầu (vắng ⇒ bậc của kết quả sự kiện đầu tiên có thẻ) — chỉ để đọc, không lọc lại.
 */
export function composeGreeting(state: JourneyResult, appended: readonly JourneyResult[], library: readonly GuideCard[] = GUIDE_CARD_LIBRARY): JourneyResult {
  const seen = new Set(state.cards.map((c) => c.id));
  const fresh = (r: JourneyResult): readonly JourneyItem[] => r.cards.filter((c) => (seen.has(c.id) ? false : (seen.add(c.id), true)));
  const isEvent = (r: JourneyResult): boolean => (GUIDE_EVENT_TRIGGERS as readonly GuideTrigger[]).includes(r.trigger);
  const stateExtra = appended.filter((r) => !isEvent(r)).flatMap(fresh);
  const events = appended.filter(isEvent);
  const eventExtra = events.flatMap(fresh);
  if (stateExtra.length === 0 && eventExtra.length === 0) return state;
  const stateCards = stateExtra.length === 0 ? state.cards : sortGuideItems([...state.cards, ...stateExtra], library);
  return { trigger: state.trigger, band: stateCards[0]?.band ?? events.find((r) => r.band !== null)?.band ?? null, cards: [...stateCards, ...eventExtra] };
}

/*
 * 🔴🔴 GUIDE-MVP-NATURAL-COPY (@UC0182 · QĐ người dùng/PO 17/10: lời chào "cứng", đồng ý cho "LLM viết lời chào") — GIỌNG của lượt dẫn đường.
 *
 * QĐ điều phối giữ §4.3 *"câu chữ thẻ ở catalog — tầng trình bày không bịa câu"* và cam kết "bấm nút = 0 LLM": câu do LLM viết OFFLINE vào catalog,
 * runtime KHÔNG gọi LLM (0 token, 0 độ trễ). Lời mở đầu và câu nối dưới đây là MỘT PHẦN của catalog (hằng, PO duyệt được như câu thẻ), chọn TẤT ĐỊNH:
 *  - Lời MỞ ĐẦU theo trigger — chỉ lượt người dùng tự MỞ trò chuyện (`OPEN` · `RETURN_AFTER_ABSENCE`); lượt "làm gì tiếp" (`ASKED_NEXT`) đang ở giữa
 *    cuộc nói chuyện ⇒ không chào lại.
 *  - Câu NỐI theo VỊ TRÍ thẻ (thẻ thứ hai "Ngoài ra, …", thứ ba "Thêm nữa, …", …); thẻ bậc HYGIENE (cảnh báo STK vừa đổi · vai vừa đổi) ⇒ "Lưu ý, …".
 *    Nối = hạ chữ đầu câu thẻ, CHỈ khi thẻ có trong thư viện và MỌI câu của nó mở bằng CHỮ của catalog (`GUIDE_LITERAL_LEAD_CARD_IDS`): câu mở bằng
 *    GIÁ TRỊ (tên đợt · câu lời mời — chữ người dùng đặt) hay thẻ lạ ⇒ để nguyên, không nối.
 * 🔴 Lời nối KHÔNG đổi nội dung câu thẻ: `guideLineCore` tách ra đúng câu gốc (lưới `compose.test.ts` tự khám phá MỌI câu thẻ) — máy đo/oracle đọc câu
 * thẻ qua đó. Không chỗ trống, không con số, không lời mời nút trong lời mở/nối (cùng lưới).
 */

/** Lời mở đầu theo trigger của lượt. Vắng ⇒ không mở đầu. */
export const GUIDE_GREETING_OPENERS: Readonly<Partial<Record<GuideTrigger, string>>> = Object.freeze({
  OPEN: "Chào bạn!",
  RETURN_AFTER_ABSENCE: "Chào bạn!",
});

/** Câu nối của thẻ ở vị trí 1, 2, 3… (xoay vòng) — theo THỨ TỰ thẻ, không ngẫu nhiên. */
export const GUIDE_LINE_CONNECTORS: readonly string[] = Object.freeze(["Ngoài ra, ", "Thêm nữa, ", "Bên cạnh đó, "]);

/** Câu nối của thẻ bậc HYGIENE (không đứng đầu): một điều cần để ý, không phải "thêm một việc". */
export const GUIDE_HEADS_UP_CONNECTOR = "Lưu ý, ";

const LOWERABLE_LEAD_RE = /^\p{Lu}\p{Ll}/u;

/** Thẻ mà MỌI câu (gốc · biến thể · lối đúng) mở bằng một chữ của catalog — chỉ những thẻ này được nối (hạ chữ đầu). */
export const GUIDE_LITERAL_LEAD_CARD_IDS: ReadonlySet<string> = Object.freeze(
  new Set(
    GUIDE_CARD_LIBRARY.filter((c) =>
      [c.templateVi, ...[...(c.variants ?? []), ...(c.doors ?? [])].flatMap((v) => (typeof v.templateVi === "string" ? [v.templateVi] : []))].every((t) =>
        LOWERABLE_LEAD_RE.test(t),
      ),
    ).map((c) => c.id),
  ),
);

/** Một dòng của lượt như giọng nhìn thấy: id thẻ (tra thư viện) · bậc · câu đã thế chỗ trống. */
export interface GuideVoiceItem {
  readonly id: string;
  readonly band: GuideBand;
  readonly text: string;
}

function connectorAt(index: number, band: GuideBand): string {
  if (band === "HYGIENE") return GUIDE_HEADS_UP_CONNECTOR;
  return GUIDE_LINE_CONNECTORS[(index - 1) % GUIDE_LINE_CONNECTORS.length]!;
}

/** Ghép câu các thẻ thành chữ của lượt: lời mở đầu (theo trigger) + câu nối (theo vị trí) — mỗi thẻ vẫn MỘT dòng. Hàm thuần, tất định. */
export function guideVoiceText(trigger: GuideTrigger, items: readonly GuideVoiceItem[]): string {
  const lines = items.map((item, i) => {
    if (i === 0 || !GUIDE_LITERAL_LEAD_CARD_IDS.has(item.id) || !LOWERABLE_LEAD_RE.test(item.text)) return item.text;
    return `${connectorAt(i, item.band)}${item.text.charAt(0).toLocaleLowerCase("vi")}${item.text.slice(1)}`;
  });
  const opener = GUIDE_GREETING_OPENERS[trigger];
  if (opener !== undefined && lines.length > 0) lines[0] = `${opener} ${lines[0]!}`;
  return lines.join("\n");
}

/** Ngược của `guideVoiceText` cho MỘT dòng: bỏ lời mở đầu / câu nối, trả câu thẻ gốc (nâng lại chữ đầu đã hạ). Dòng không mang giọng ⇒ nguyên văn. */
export function guideLineCore(line: string): string {
  let s = line;
  for (const opener of new Set(Object.values(GUIDE_GREETING_OPENERS))) {
    if (s.startsWith(`${opener} `)) {
      s = s.slice(opener.length + 1);
      break;
    }
  }
  for (const connector of [...GUIDE_LINE_CONNECTORS, GUIDE_HEADS_UP_CONNECTOR]) {
    if (s.startsWith(connector)) return `${s.charAt(connector.length).toLocaleUpperCase("vi")}${s.slice(connector.length + 1)}`;
  }
  return s;
}
